import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * CRMCalendar — month grid showing contact activity.
 *
 * Each contact is placed on the day it was last updated (falls back to
 * created_at). Days are clickable → a bottom panel lists that day's
 * contacts. Colored pills reflect the contact's current stage.
 *
 * Props: stages, grouped, showArchived, onEdit, filterQuery.
 */
export default function CRMCalendar({ stages, grouped, showArchived, onEdit, filterQuery }) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const stageMap = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, s])), [stages]);

  // Flatten contacts into a { 'YYYY-MM-DD': [contacts] } map for the current month.
  const contactsByDay = useMemo(() => {
    const map = {};
    Object.entries(grouped).forEach(([stageId, list]) => {
      const s = stageMap[stageId];
      if (!s) return;
      if (!showArchived && s.is_archived) return;
      list.forEach((c) => {
        const iso = c.updated_at || c.created_at;
        if (!iso) return;
        const d = new Date(iso);
        if (isNaN(d)) return;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (!map[key]) map[key] = [];
        map[key].push({ ...c, _stage: s });
      });
    });
    const q = (filterQuery || '').toLowerCase();
    if (q) {
      Object.keys(map).forEach((k) => {
        map[k] = map[k].filter(
          (c) =>
            c.name?.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q) ||
            c.company?.toLowerCase().includes(q),
        );
      });
    }
    return map;
  }, [grouped, stages, stageMap, showArchived, filterQuery]);

  // Build a 6-row × 7-col grid for the current month.
  const cells = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startWeekday = first.getDay(); // 0 = Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = [];
    // Padding for the first week
    for (let i = 0; i < startWeekday; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      arr.push({ day: d, key });
    }
    // Pad to 42 cells (6 rows × 7)
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const selectedList = selectedDay ? contactsByDay[selectedDay] || [] : [];

  const shift = (delta) => {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
    setSelectedDay(null);
  };

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="crm-calendar-view">
      <div className="bg-brand-surface rounded-xl border border-white/5 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-playfair text-white" data-testid="calendar-month-label">{monthLabel}</h3>
          <div className="flex gap-1">
            <button onClick={() => shift(-1)} className="w-8 h-8 rounded-md border border-white/10 hover:bg-white/5 text-gray-300" data-testid="calendar-prev">
              <ChevronLeft className="w-4 h-4 mx-auto" />
            </button>
            <button onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} className="px-3 h-8 rounded-md border border-white/10 hover:bg-white/5 text-gray-300 text-xs">
              Today
            </button>
            <button onClick={() => shift(1)} className="w-8 h-8 rounded-md border border-white/10 hover:bg-white/5 text-gray-300" data-testid="calendar-next">
              <ChevronRight className="w-4 h-4 mx-auto" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider text-gray-500 text-center py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={idx} className="aspect-square" />;
            const list = contactsByDay[cell.key] || [];
            const isToday = cell.key === todayKey;
            const isSelected = cell.key === selectedDay;
            return (
              <button
                key={cell.key}
                onClick={() => setSelectedDay(cell.key)}
                className={`aspect-square rounded-md border p-1.5 text-left transition ${
                  isSelected
                    ? 'border-brand-gold bg-brand-gold/10'
                    : isToday
                      ? 'border-brand-blue/60 bg-brand-blue/5'
                      : 'border-white/5 hover:border-white/20 hover:bg-white/[0.02]'
                }`}
                data-testid={`day-${cell.key}`}
              >
                <div className={`text-xs font-medium ${isToday ? 'text-brand-blue' : 'text-white'}`}>{cell.day}</div>
                {list.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-0.5">
                    {list.slice(0, 3).map((c) => (
                      <span
                        key={c.id}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: c._stage?.color || '#94a3b8' }}
                        title={c.name}
                      />
                    ))}
                    {list.length > 3 && (
                      <span className="text-[9px] text-gray-400 ml-0.5">+{list.length - 3}</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div className="mt-4 bg-brand-surface rounded-xl border border-white/5 p-4" data-testid="calendar-day-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-white">
              {new Date(selectedDay + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </h4>
            <span className="text-xs text-gray-400">
              {selectedList.length} {selectedList.length === 1 ? 'contact' : 'contacts'}
            </span>
          </div>
          {selectedList.length === 0 ? (
            <p className="text-xs text-gray-500 py-4 text-center">No contact activity on this day.</p>
          ) : (
            <div className="space-y-2">
              {selectedList.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onEdit(c)}
                  className="w-full text-left p-3 rounded-lg bg-brand-surface2 border border-white/5 hover:border-brand-gold/30 transition"
                  data-testid={`calendar-contact-${c.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white font-medium">{c.name}</span>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider"
                      style={{ backgroundColor: `${c._stage.color}22`, color: c._stage.color }}
                    >
                      {c._stage.label}
                    </span>
                  </div>
                  {c.company && <p className="text-[11px] text-gray-400 mt-1">{c.company}</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
